import { useMemo } from 'react';
import { defineMessages } from 'react-intl';

import { SortingOption } from '@sorare/core/src/__generated__/globalTypes';
import { DropdownSelect } from '@sorare/core/src/atoms/inputs/DropdownSelect';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useQueryState } from '@sorare/core/src/hooks/useQueryState';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';

import { DefaultSortType } from './types';

const messages = defineMessages({
  latest: {
    id: 'useSortByDate.latest',
    defaultMessage: 'Latest',
  },
  oldest: {
    id: 'useSortByDate.oldest',
    defaultMessage: 'Oldest',
  },
});

export const useSortByDate = () => {
  const { formatMessage } = useIntlContext();

  const [{ sortType }, setSortType] = useQueryState({
    sortType: DefaultSortType.LATEST,
  });

  const onSortTypeChange = (item: {
    label: string;
    value: DefaultSortType;
  }) => {
    setSortType({ sortType: item.value });
  };

  const sortAsVariable =
    sortType === DefaultSortType.LATEST
      ? SortingOption.DESC
      : SortingOption.ASC;

  const sortOptions = useMemo(
    () =>
      Object.values(DefaultSortType).map(element => ({
        label: formatMessage(
          messages[toCamelCase(element) as keyof typeof messages]
        ),
        value: element,
      })),
    [formatMessage]
  );

  const SortSelect = () => (
    <DropdownSelect
      size="medium"
      value={sortType}
      options={sortOptions}
      onChange={value => {
        const selectedOption = sortOptions.find(o => o.value === value);
        if (selectedOption) {
          onSortTypeChange(selectedOption);
        }
      }}
    />
  );

  return { sortOptions, sortType, SortSelect, sortAsVariable };
};
